import { FC, useCallback, useContext, useEffect, useMemo } from 'react';

import { Typography, Form } from 'antd';

import {
  ColSize,
  InputCurrencyForm,
  InputForm,
  InputNumberForm,
  RadioForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../shared/components';


import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../../../../shared';
import { StepTwoFormContext } from '../../../../contexts/StepTwoForm';

import { ValidateForm } from '../../../../../../../../../../../shared/utils';

import { COLORS } from '../../../../../../../../../../../config/themes/colors';
import {
  DECISION_TYPE,
  OPTION_YES_OR_NOT,
  Version,
} from '../../../../../../../../../../../shared/interfaces';
import { WizardDataFormContext } from '../../../../../../Wizard/contexts/WizardDataForm';

interface GeneralDataProps {
  duplicateId?: number;
  id: number;
}

export const GeneralData: FC<GeneralDataProps> = ({ duplicateId, id }) => {
  const validateForm = useMemo(() => new ValidateForm(), []);

  const {
    stepTwoForm,
    removeSelectedCoverageByVehicle,
    setVersionSelectedByVehicle,
  } = useContext(WizardDataFormContext);
  const { savedQuotation, loadingSavedQuotation, quotationInfo, quotationToRenewal, loadingRenewalQuotation } =
    useContext(QuotationInfoContext);
  const {
      clientSelected,
      stepOneForm
  } = useContext(StepOneFormContext);
  const {
    getModelByBrandAndYear,
    getVersionByYearBrandAndModel,
    isLoadingModelList,
    isLoadingVersionList,
    yearList,
    showWebDiscountRate,
    getBrandsByYear,
    brandsByYear,
    isLoadingBrandList,
    modelsByBrandAndYear,
    versionsByYearBrandAndModel,
    goodResultTypesList,
    getVersionSelectedByVehicle,
  } = useContext(StepTwoFormContext);
  const {
    getLocalitiesPerProvince,
    isLoadingLocalities,
    isLoadingProvinceList,
    getLocalitiesByProvinceFromState,
    provinceList,
    setLocalities,
    allLocalitiesPerProvince,
  } = useContext(GenericDataFormContext);

  const gncRadioFieldName = `gnc-tab-${id}`;
  const hasGNC = Form.useWatch(gncRadioFieldName, stepTwoForm);

  const yearFieldName = `vehicleYear-tab-${id}`;
  const yearSelected = Form.useWatch(yearFieldName, stepTwoForm);

  const brandFieldName = `brand-tab-${id}`;
  const brandSelected = Form.useWatch(brandFieldName, stepTwoForm);

  const modelFieldName = `model-tab-${id}`;
  const modelSelected = Form.useWatch(modelFieldName, stepTwoForm);

  const versionFieldName = `version-tab-${id}`;
  const versionSelectedForm = Form.useWatch(versionFieldName, stepTwoForm);

  const provinceFieldName = `province-tab-${id}`;
  const provinceSelected = Form.useWatch(provinceFieldName, stepTwoForm);

  const locationFieldName = `location-tab-${id}`;
  const zoneFieldName = `zone-tab-${id}`;

  const infoValueFieldName = `infoValueCar-tab-${id}`;
  const originFieldName = `origin-tab-${id}`;
  const vehicleTypeFieldName = `vehicleType-tab-${id}`;

  const gncValueFieldName = `gncValue-tab-${id}`;

  const isIntegralClientFieldName = `integralClient-tab-${id}`;
  const discountWebFieldName = `webDiscount-tab-${id}`;
  const goodResultFieldName = `goodResult-tab-${id}`;

  const isRenewal = useMemo(() => Boolean(quotationToRenewal), [quotationToRenewal]);

  const brands = useMemo(
    () => brandsByYear.find((el) => el.year === yearSelected)?.brands ?? [],
    [brandsByYear, yearSelected]
  );

  const models = useMemo(
    () =>
      modelsByBrandAndYear.find(
        (el) => el.year === yearSelected && el.brand === brandSelected
      )?.models ?? [],
    [modelsByBrandAndYear, yearSelected, brandSelected]
  );

  const versionOptions = useMemo(
    () =>
      versionsByYearBrandAndModel.find(
        (el) =>
          el.year === yearSelected &&
          el.brand === brandSelected &&
          el.model === modelSelected
      )?.versionOptions ?? [],
    [versionsByYearBrandAndModel, yearSelected, modelSelected, brandSelected]
  );

  const localities = useMemo(
    () => getLocalitiesByProvinceFromState(provinceSelected),
    [getLocalitiesByProvinceFromState, provinceSelected]
  );

  const onSelectProvince = useCallback(() => {
    stepTwoForm?.resetFields([locationFieldName, zoneFieldName]);
    setLocalities([]);
  }, [stepTwoForm, setLocalities, locationFieldName, zoneFieldName]);

  const versions = useMemo(
    () =>
      versionsByYearBrandAndModel.find(
        (el) =>
          el.year === yearSelected &&
          el.brand === brandSelected &&
          el.model === modelSelected
      )?.versions ?? [],
    [versionsByYearBrandAndModel, yearSelected, modelSelected, brandSelected]
  );
  
  const versionSelected = useMemo(
    () => getVersionSelectedByVehicle(String(id)),
    [getVersionSelectedByVehicle, id]
  );

  const onSelectVersion = useCallback(
    (versionCode: string) => {
      stepTwoForm?.resetFields([
        vehicleTypeFieldName,
        originFieldName,
        infoValueFieldName,
      ]);

      const version = versions?.find(
        (el: Version) => String(el.codigoVersion) === String(versionCode)
      );

      if (version) {
        setVersionSelectedByVehicle((prevValues) => {
          const filteredValues = prevValues.filter(
            (el) => el.componentId !== String(id)
          );
          return [
            ...filteredValues,
            { componentId: String(id), versionSelected: version },
          ];
        });
        stepTwoForm?.setFieldValue(
          vehicleTypeFieldName,
          version?.descripcionTipoDeVehiculo
        );
        stepTwoForm?.setFieldValue(originFieldName, version?.descripcionOrigen);
        stepTwoForm?.setFieldValue(
          infoValueFieldName,
          version?.valorInfoautoDefecto
        );
        removeSelectedCoverageByVehicle(String(id));
      }
    },
    [
      versions,
      stepTwoForm,
      setVersionSelectedByVehicle,
      infoValueFieldName,
      originFieldName,
      vehicleTypeFieldName,
      id,
      removeSelectedCoverageByVehicle,
    ]
  );

  const handleDuplicatePlan = useCallback(() => {
    if (duplicateId) {
      const allFieldsValues = stepTwoForm?.getFieldsValue(true);

      const duplicatedFieldsValues = Object.keys(allFieldsValues).reduce(
        (acc, key) => {
          if (key.includes(`-tab-${duplicateId}`)) {
            const newKey = key.replace(`-tab-${duplicateId}`, `-tab-${id}`);
            acc[newKey] = allFieldsValues[key];
          }

          return acc;
        },
        {} as Record<string, any>
      );

      const versionCode = stepTwoForm?.getFieldValue(
        `version-tab-${duplicateId}`
      );

      onSelectVersion(versionCode);

      stepTwoForm?.setFieldsValue(duplicatedFieldsValues);
    }
  }, [duplicateId, stepTwoForm, id, onSelectVersion]);

  const handleChangeYear = useCallback(
    async (year: string) => {
      await getBrandsByYear(year);
      stepTwoForm?.resetFields([
        brandFieldName,
        modelFieldName,
        versionFieldName,
        infoValueFieldName,
        originFieldName,
        vehicleTypeFieldName,
      ]);
    },
    [
      getBrandsByYear,
      stepTwoForm,
      brandFieldName,
      modelFieldName,
      versionFieldName,
      infoValueFieldName,
      originFieldName,
      vehicleTypeFieldName,
    ]
  );

  const handleChangeBrand = useCallback(
    async (brand: string) => {
      await getModelByBrandAndYear(yearSelected, brand);
      stepTwoForm?.resetFields([
        modelFieldName,
        versionFieldName,
        infoValueFieldName,
        originFieldName,
        vehicleTypeFieldName,
      ]);
    },
    [
      getModelByBrandAndYear,
      stepTwoForm,
      yearSelected,
      modelFieldName,
      versionFieldName,
      infoValueFieldName,
      originFieldName,
      vehicleTypeFieldName,
    ]
  );

  const handleChangeModel = useCallback(
    async (model: string) => {
      await getVersionByYearBrandAndModel(yearSelected, brandSelected, model);
      stepTwoForm?.resetFields([
        versionFieldName,
        infoValueFieldName,
        originFieldName,
        vehicleTypeFieldName,
      ]);
    },
    [
      getVersionByYearBrandAndModel,
      yearSelected,
      brandSelected,
      stepTwoForm,
      versionFieldName,
      infoValueFieldName,
      originFieldName,
      vehicleTypeFieldName,
    ]
  );

  const onSelectLocality = useCallback(
    (locality: string) => {
      const provinceLocalities = allLocalitiesPerProvince?.find(
        (province) => String(province.provinceCode) === String(provinceSelected)
      )?.localities;

      const localityInfo = provinceLocalities?.find(
        ({ codigoPostal, sufijoCodigoPostal }) =>
          `${sufijoCodigoPostal}${codigoPostal}` === locality
      );

      stepTwoForm?.setFieldValue(zoneFieldName, localityInfo?.zonaAutos);
    },
    [allLocalitiesPerProvince, stepTwoForm, zoneFieldName, provinceSelected]
  );

  const gncValueRules = useMemo(
    () =>
      hasGNC === DECISION_TYPE.YES
        ? validateForm.generateRules({
            label: 'valor del gnc',
            rules: ['required'],
          })
        : undefined,
    [hasGNC, validateForm]
  );
    
  const infoValueRules = useMemo(
    () =>
      validateForm.generateRules({
        label: 'valor de información del auto',
        rules: [
          'required',
          `range:${Number(versionSelected?.valorInfoautoMinimo)}:${Number(
            versionSelected?.valorInfoautoMaximo
          )}`,
        ],
      }),
    [versionSelected, validateForm]
  );

  useEffect(() => {        
    handleDuplicatePlan();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (provinceSelected?.length) {
      getLocalitiesPerProvince(provinceSelected);
    }
  }, [provinceSelected, getLocalitiesPerProvince]);

  useEffect(() => {
    if (!versionSelected && versionSelectedForm) {
      onSelectVersion(versionSelectedForm);
    }
  }, [versionSelected, versionSelectedForm, onSelectVersion]);

  useEffect(() => {        
    if (!isLoadingProvinceList && clientSelected && stepOneForm?.province) {
      
      const localities = getLocalitiesByProvinceFromState(stepOneForm!.province!)
      const userProvinceSelected = provinceList?.find(e => e.value == stepOneForm!.province)
      const userLocalitieSelected = localities?.find(e => e.value == stepOneForm!.location);
      
      stepTwoForm?.setFieldValue(provinceFieldName, userProvinceSelected);
      stepTwoForm?.setFieldValue(locationFieldName, userLocalitieSelected);
      
      const provinceLocalities = allLocalitiesPerProvince?.find(
        (province) => String(province.provinceCode) === String(userProvinceSelected?.value)
      )?.localities;

      const localityInfo = provinceLocalities?.find(
        ({ codigoPostal, sufijoCodigoPostal }) =>
          `${sufijoCodigoPostal}${codigoPostal}` === userLocalitieSelected?.value
      );
      
      stepTwoForm?.setFieldValue(zoneFieldName, localityInfo?.zonaAutos);
    }
  },[isLoadingProvinceList, isLoadingLocalities]);

  useEffect(() => {},[]);


  return (
    <>
      <RowSize>
        <Typography.Title
          level={3}
          style={{
            margin: 0,
            marginBottom: 10,
            color: COLORS.secondary[100],
          }}
        >
          Datos Generales
        </Typography.Title>
      </RowSize>

      <RowSize>
        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Año Vehículo:',
              name: yearFieldName,
              rules: validateForm.generateRules({
                label: 'año vehículo',
                rules: ['required'],
              }),
            }}
            selectProps={{
              loading: !yearList.length,
              onSelect: handleChangeYear,
              options: yearList,
              showSearch: true,
              filterSort: undefined,
              disabled: isRenewal,
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Marca:',
              name: brandFieldName,
              rules: validateForm.generateRules({
                label: 'marca',
                rules: ['required'],
              }),
            }}
            selectProps={{
              disabled: isRenewal || !brands.length || isLoadingBrandList,
              loading: isLoadingBrandList,
              onSelect: handleChangeBrand,
              options: brands,
              showSearch: true,
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Modelo:',
              name: modelFieldName,
              rules: validateForm.generateRules({
                label: 'modelo',
                rules: ['required'],
              }),
            }}
            selectProps={{
              disabled: isRenewal || !models.length || isLoadingModelList || !brandSelected,
              loading: isLoadingModelList,
              onSelect: handleChangeModel,
              options: models,
              showSearch: true,
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Versión:',
              name: versionFieldName,
              rules: validateForm.generateRules({
                label: 'versión',
                rules: ['required'],
              }),
            }}
            selectProps={{
              disabled: isRenewal || isLoadingVersionList || !modelSelected,
              loading: isLoadingVersionList,
              options: versionOptions,
              showSearch: true,
              onSelect: onSelectVersion,
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <RadioForm
            formProps={{
              initialValue:
                ( savedQuotation && loadingSavedQuotation ) || (quotationToRenewal && loadingRenewalQuotation)
                  ? undefined
                  : DECISION_TYPE.NO,
              label: 'GNC:',
              name: gncRadioFieldName,
            }}
            radioProps={{
              onChange: () => {
                stepTwoForm?.resetFields([gncValueFieldName]);
              },
              disabled: isRenewal,
            }}
            options={OPTION_YES_OR_NOT}
          />
        </ColSize>

        <ColSize size={25}>
          <InputCurrencyForm
            formProps={{
              label: 'Valor del GNC:',
              name: gncValueFieldName,
              rules: gncValueRules,
            }}
            inputCurrencyProps={{
               disabled: isRenewal || hasGNC === DECISION_TYPE.NO,
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <InputCurrencyForm
            formProps={{
              label: 'Valor Info Auto:',
              name: infoValueFieldName,
              rules: infoValueRules,
            }}
            inputCurrencyProps={{
              disabled: isRenewal ? false : !versionSelectedForm,
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Tipo de Vehículo:',
              name: vehicleTypeFieldName,
            }}
            inputProps={{ disabled: true }}
          />
        </ColSize>

        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Origen:',
              name: originFieldName,
            }}
            inputProps={{ disabled: true }}
          />
        </ColSize>

        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Provincia:',
              name: provinceFieldName,
              rules: validateForm.generateRules({
                label: 'provincia',
                rules: ['required'],
              }),
            }}
            selectProps={{
              loading: isLoadingProvinceList,
              onSelect: onSelectProvince,
              options: provinceList,
              showSearch: true,
              disabled: true //TODO: se debe borrar parte de la logica vieja para elegir provincia cuando se apruebe
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Localidad:',
              name: locationFieldName,
              rules: validateForm.generateRules({
                label: 'localidad',
                rules: ['required'],
              }),
            }}
            selectProps={{
              disabled: isRenewal || !localities?.length || isLoadingLocalities,
              loading: isLoadingLocalities,
              options: localities,
              showSearch: true,
              onSelect: (locality) => onSelectLocality(locality),
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <InputForm
            formProps={{
              label: 'Zona:',
              name: zoneFieldName,
            }}
            inputProps={{ disabled: true }}
          />
        </ColSize>

        <ColSize size={25}>
          <RadioForm
            formProps={{
              initialValue:
              ( savedQuotation && loadingSavedQuotation ) || (quotationToRenewal && loadingRenewalQuotation)
                  ? undefined
                  : DECISION_TYPE.NO,
              label: 'Cliente Integral:',
              name: isIntegralClientFieldName,
            }}
            options={OPTION_YES_OR_NOT}
            radioProps={{ disabled: isRenewal }}
          />
        </ColSize>

        {showWebDiscountRate?.tieneDescuentoWeb && (
          <ColSize size={25}>
            <InputNumberForm
              formProps={{
                label: 'Descuento web:',
                name: discountWebFieldName,
                rules: [
                  { type: 'number', min: showWebDiscountRate.descuentoWebMinimo, message: `El valor no puede ser menor que ${showWebDiscountRate.descuentoWebMinimo}`},
                  { type: 'number', max: showWebDiscountRate.descuentoWebMaximo, message: `El valor no puede superar el maximo de ${showWebDiscountRate.descuentoWebMaximo}` }
                ],
                initialValue: showWebDiscountRate.descuentoWebDefecto
              }}
              inputNumberProps={{ placeholder: '0', disabled: isRenewal}}
            />
          </ColSize>
        )}

        {showWebDiscountRate?.tieneBuenResultado && (
          <ColSize size={25}>
            <SelectForm
              formProps={{
                label: 'Buen resultado:',
                name: goodResultFieldName,
              }}
              selectProps={{
                options: goodResultTypesList,
                disabled: isRenewal || !goodResultTypesList.length,
              }}
            />
          </ColSize>
        )}
      </RowSize>
    </>
  );
};
